import { useCallback, useEffect, useRef, useState } from 'react';

import { useApolloClient } from '@apollo/client';
import type { ApolloClient } from '@apollo/client';

import {
  GetComboShellByIdsDocument,
  GetItemsByIdsByVendorDocument,
  GetPickerShellDocument,
} from 'generated/sanity-graphql';
import { simpleCacheQuery } from 'state/graphql/with-simple-cache';
import { useSanityGqlEndpoint } from 'state/network';

import { IGetPickerPlusByVendorQuery, VendorVariables } from './picker-batched-query-types';

// Helpers

/** Split an array into chunks of at most `size` elements */
const chunk = <T>(arr: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

// Sanity's allItems/_id_in uses string IDs (not ID scalars)
// Strip the "drafts." prefix if present so the lookup works
const normalizeId = (id: string) => id.replace(/^drafts\./, '');

// Types

type AbortRef = { current: boolean };

interface UsePickerBatchedQueryOptions {
  id: string;
  skip?: boolean;
  vendorVariables: VendorVariables;
}

interface UsePickerBatchedQueryResult {
  data: IGetPickerPlusByVendorQuery | undefined;
  loading: boolean;
  error: Error | undefined;
  refetch: () => Promise<void>;
}

// Batch sizes
// Tuned so each batch response stays well under the 5MB limit.
const ITEM_BATCH_SIZE = 20;
const COMBO_SHELL_BATCH_SIZE = 25; // Shell query is shallow, no modifier trees.

// Hook

export const fetchPickerBatchedAsync = async (
  client: ApolloClient<any>,
  uri: string,
  id: string,
  vendorVariables: VendorVariables,
  abortRef?: AbortRef
): Promise<IGetPickerPlusByVendorQuery | null> => {
  if (!id) {
    return null;
  }

  // Pass 1: shallow picker shell, just top-level IDs.
  const shellResult = await simpleCacheQuery(client, {
    query: GetPickerShellDocument,
    variables: { id },
    context: { uri },
  });

  if (abortRef?.current) {
    return null;
  }

  const shell = shellResult?.Picker;
  if (!shell) {
    return { Picker: null } as any;
  }

  // Partition top-level picker option IDs by type
  const pickerItemIds: string[] = [];
  const comboIds: string[] = [];

  for (const pickerOption of shell.options ?? []) {
    const option = pickerOption?.option;
    if (!option) {
      continue;
    }
    const rawId = option._id;
    if (!rawId) {
      continue;
    }
    const nid = normalizeId(rawId);
    if (option._type === 'item') {
      pickerItemIds.push(nid);
    } else if (option._type === 'combo') {
      comboIds.push(nid);
    }
  }

  // Pass 2: fetch direct items and combo shells in parallel.
  const itemChunks = chunk(pickerItemIds, ITEM_BATCH_SIZE);
  const comboShellChunks = chunk(comboIds, COMBO_SHELL_BATCH_SIZE);

  const [itemBatches, comboShellBatches] = await Promise.all([
    Promise.all(
      itemChunks.map(ids =>
        simpleCacheQuery(client, {
          query: GetItemsByIdsByVendorDocument,
          variables: { ids, ...vendorVariables },
          context: { uri },
        })
      )
    ),
    Promise.all(
      comboShellChunks.map(ids =>
        simpleCacheQuery(client, {
          query: GetComboShellByIdsDocument,
          variables: { ids, ...vendorVariables },
          context: { uri },
        })
      )
    ),
  ]);

  if (abortRef?.current) {
    return null;
  }

  // Build item map from Pass 2a (picker direct items)
  const itemMap = new Map<string, any>(
    itemBatches
      .flatMap((r: any) => r?.allItems ?? [])
      .map((item: { _id: string }) => [normalizeId(item._id), item])
  );

  // Collect all internal item IDs from combo shells (mainItem + slot options)
  const comboShells = comboShellBatches.flatMap((r: any) => r?.allCombos ?? []);

  const comboInternalItemIds = new Set<string>();
  for (const combo of comboShells) {
    if (combo?.mainItem?._id) {
      comboInternalItemIds.add(normalizeId(combo.mainItem._id));
    }
    for (const slot of combo?.options ?? []) {
      for (const slotOption of slot?.options ?? []) {
        const slotItem = slotOption?.option;
        if (slotItem?._id && slotItem?._type === 'item') {
          comboInternalItemIds.add(normalizeId(slotItem._id));
        }
      }
    }
  }

  // Only fetch combo-internal items not already in itemMap from Pass 2a
  const missingItemIds = [...comboInternalItemIds].filter(iid => !itemMap.has(iid));

  // Pass 3: resolve combo-internal items, if any new IDs were discovered.
  if (missingItemIds.length > 0) {
    const missingChunks = chunk(missingItemIds, ITEM_BATCH_SIZE);
    const missingBatches = await Promise.all(
      missingChunks.map(ids =>
        simpleCacheQuery(client, {
          query: GetItemsByIdsByVendorDocument,
          variables: { ids, ...vendorVariables },
          context: { uri },
        })
      )
    );

    if (abortRef?.current) {
      return null;
    }

    for (const batch of missingBatches) {
      for (const item of batch?.allItems ?? []) {
        itemMap.set(normalizeId(item._id), item);
      }
    }
  }

  // Assemble combos: merge shell data with resolved items.
  const comboMap = new Map<string, any>();
  for (const combo of comboShells) {
    if (!combo?._id) {
      continue;
    }

    const resolvedMainItem = combo.mainItem?._id
      ? itemMap.get(normalizeId(combo.mainItem._id)) ?? combo.mainItem
      : null;

    const resolvedOptions = (combo.options ?? []).map((slot: any) => ({
      ...slot,
      options: (slot.options ?? []).map((slotOption: any) => {
        const slotItem = slotOption?.option;
        if (!slotItem?._id) {
          return slotOption;
        }
        const resolvedSlotItem = itemMap.get(normalizeId(slotItem._id)) ?? slotItem;
        return { ...slotOption, option: resolvedSlotItem };
      }),
    }));

    comboMap.set(normalizeId(combo._id), {
      ...combo,
      mainItem: resolvedMainItem,
      options: resolvedOptions,
    });
  }

  // Final assembly: rebuild picker options.
  const assembledOptions = (shell.options ?? []).map((pickerOption: any) => {
    const optionId = normalizeId(pickerOption?.option?._id ?? '');
    const optionType = pickerOption?.option?._type;

    const resolvedOption =
      optionType === 'item'
        ? itemMap.get(optionId)
        : optionType === 'combo'
        ? comboMap.get(optionId)
        : pickerOption?.option;

    return {
      ...pickerOption,
      option: resolvedOption ?? pickerOption?.option,
    };
  });

  return {
    Picker: {
      ...shell,
      options: assembledOptions,
    } as any,
  };
};

/**
 * Batched picker fetching strategy for stores where the monolithic
 * GetPickerPlus response exceeds Sanity's 5MB limit.
 * This is a temporary mitigation for the Sanity GraphQL API upgrade. The
 * preferred long-term direction is a precomputed JSON menu payload served from
 * the CMS/CDN layer, instead of reconstructing a large menu graph in the client.
 *
 * Pass 1 - GetPickerShell:
 *   Fetches only the picker metadata + top-level option IDs (~50KB).
 *
 * Pass 2 - Parallel:
 *   2a. GetItemsByIdsByVendor: full item data in batches (picker direct items)
 *   2b. GetComboShellByIds: combo metadata + internal item IDs only (no modifier tree)
 *
 * Pass 3 - GetItemsByIdsByVendor (combo internal items):
 *   Resolves the items discovered inside combo shells using the same batched
 *   item query. Deduped against items already fetched in Pass 2a.
 *
 * Final assembly merges combo shells with resolved items to reconstruct the
 * full picker shape without ever exceeding 5MB per request.
 */
export const usePickerBatchedQuery = ({
  id,
  skip = false,
  vendorVariables,
}: UsePickerBatchedQueryOptions): UsePickerBatchedQueryResult => {
  const client = useApolloClient();
  const uri = useSanityGqlEndpoint();

  const [data, setData] = useState<IGetPickerPlusByVendorQuery | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  // Keep the latest vendorVariables in a ref so fetchPicker always reads
  // the current value without needing it as a useCallback/useEffect dep.
  // This avoids identity-churn loops when the caller passes a new object
  // each render (even if the contents are the same).
  const vendorVariablesRef = useRef(vendorVariables);
  vendorVariablesRef.current = vendorVariables;

  // Generation counter prevents race conditions: if the user navigates
  // rapidly between pickers, the stale response is discarded because its
  // captured generation no longer matches generationRef.current.
  const generationRef = useRef(0);

  // Each request gets its own abort ref so a newer fetch does not re-enable
  // an older in-flight request.
  const activeAbortRef = useRef<AbortRef | null>(null);

  // Serialize only the values that actually matter for a re-fetch trigger.
  // Using a serialized string dep instead of the object avoids identity churn.
  const vendorKey = JSON.stringify(vendorVariables);

  const fetchPicker = useCallback(async () => {
    if (skip || !id) {
      setData(undefined);
      setLoading(false);
      setError(undefined);
      return;
    }

    const generation = ++generationRef.current;
    const abortRef: AbortRef = { current: false };
    if (activeAbortRef.current) {
      activeAbortRef.current.current = true;
    }
    activeAbortRef.current = abortRef;
    setData(undefined);
    setLoading(true);
    setError(undefined);

    try {
      const assembled = await fetchPickerBatchedAsync(
        client,
        uri,
        id,
        vendorVariablesRef.current,
        abortRef
      );

      // Discard results from stale fetches, e.g. rapid picker navigation.
      if (generation !== generationRef.current) {
        return;
      }
      if (!assembled) {
        return;
      }

      setData(assembled);
    } catch (err) {
      if (generation !== generationRef.current) {
        return;
      }
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      if (generation === generationRef.current) {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, uri, id, skip, vendorKey]);

  useEffect(() => {
    void fetchPicker();

    return () => {
      if (activeAbortRef.current) {
        activeAbortRef.current.current = true;
      }
      // Increment generation so any in-flight fetch from this cycle
      // fails the staleness check and discards its results.
      generationRef.current++;
    };
  }, [fetchPicker]);

  return {
    data,
    loading,
    error,
    refetch: fetchPicker,
  };
};
